import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from './NotificationsContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

export default function Layout() {
  const { user, logout } = useAuth();
  const { notifications, unread, dismiss, clearAll } = useNotifications();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.slice(0, 2) || 'fr';
  const switchLang = (lang) => i18n.changeLanguage(lang);
  const [showNotifs, setShowNotifs] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [burgerOpen, setBurgerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const userMenuRef = useRef(null);
  const notifRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Ferme les menus au changement de page
  useEffect(() => { setUserMenuOpen(false); setBurgerOpen(false); }, [location]);

  // Ferme le menu user si clic dehors
  useEffect(() => {
    const handler = (e) => { if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Ferme le panneau notifications si clic dehors
  useEffect(() => {
    const handler = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Bloque le scroll quand le menu burger est ouvert
  useEffect(() => {
    document.body.style.overflow = burgerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [burgerOpen]);

  const handleLogout = async () => {
    await logout();
    toast.success('A bientot !');
    navigate('/');
  };

  const NAV_LINKS = [
    { to: '/galerie', label: t('nav.gallery') },
    { to: '/galerie?category=peinture', label: t('nav.painting') },
    { to: '/galerie?category=photographie', label: t('nav.photo') },
    { to: '/galerie?category=sculpture', label: t('nav.sculpture') },
    { to: '/galerie?featured=true', label: t('nav.selection') },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#FDFAF5', fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Raleway:wght@300;400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #FDFAF5; color: #2C2416; }
        a { color: inherit; text-decoration: none; }

        /* ── Nav links ── */
        .nav-link { font-family: 'Raleway', sans-serif; font-size: 12px; font-weight: 500; letter-spacing: 2.5px; text-transform: uppercase; color: #5C4A2A; transition: color 0.2s; padding: 4px 0; border-bottom: 1px solid transparent; }
        .nav-link:hover, .nav-link.active { color: #2C2416; border-bottom-color: #2C2416; }

        /* ── Buttons ── */
        .btn-primary { background: #2C2416; color: #F5EDDB; font-family: 'Raleway', sans-serif; font-size: 11px; font-weight: 500; letter-spacing: 2px; text-transform: uppercase; padding: 12px 28px; border: none; cursor: pointer; transition: all 0.2s; display: inline-block; }
        .btn-primary:hover { background: #4A3820; }
        .btn-outline { background: transparent; color: #2C2416; font-family: 'Raleway', sans-serif; font-size: 11px; font-weight: 500; letter-spacing: 2px; text-transform: uppercase; padding: 11px 27px; border: 1px solid #2C2416; cursor: pointer; transition: all 0.2s; display: inline-block; }
        .btn-outline:hover { background: #2C2416; color: #F5EDDB; }
        input, textarea, select { font-family: 'Raleway', sans-serif; outline: none; }
        input:focus, textarea:focus, select:focus { border-color: #8B6F4E !important; }

        /* ── Desktop nav (visible >= 860px) ── */
        .nav-desktop { display: flex; }
        .nav-actions-desktop { display: flex; }
        .burger-btn { display: none; }

        /* ── Burger menu overlay ── */
        .burger-overlay {
          position: fixed; inset: 0; z-index: 99;
          background: #FDFAF5;
          display: flex; flex-direction: column;
          transform: translateX(100%);
          transition: transform 0.35s cubic-bezier(0.4,0,0.2,1);
        }
        .burger-overlay.open { transform: translateX(0); }
        .burger-nav-link {
          font-family: 'Raleway', sans-serif; font-size: 13px; letter-spacing: 3px; text-transform: uppercase;
          color: #5C4A2A; padding: 18px 28px; border-bottom: 1px solid #F0E8D5;
          display: block; transition: background 0.15s;
        }
        .burger-nav-link:hover { background: #F5EDDB; color: #2C2416; }
        .burger-section-title {
          font-family: 'Raleway', sans-serif; font-size: 9px; letter-spacing: 4px; text-transform: uppercase;
          color: #8B6F4E; padding: 24px 28px 10px; display: block;
        }

        /* ── Footer grid responsive ── */
        .footer-grid { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 48px; margin-bottom: 48px; }

        /* ── Responsive: tablette + mobile (< 860px) ── */
        @media (max-width: 860px) {
          .nav-desktop { display: none; }
          .nav-actions-desktop { display: none; }
          .burger-btn { display: flex; }
          .footer-grid { grid-template-columns: 1fr 1fr; gap: 32px; }
        }
        @media (max-width: 480px) {
          .footer-grid { grid-template-columns: 1fr; gap: 24px; }
        }
      `}</style>

      {/* ── HEADER ── */}
      <header style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: scrolled ? 'rgba(253,250,245,0.97)' : '#FDFAF5',
        borderBottom: '1px solid #E8DFCC',
        boxShadow: scrolled ? '0 2px 20px rgba(44,36,22,0.08)' : 'none',
        transition: 'box-shadow 0.3s',
      }}>
        <div style={{ maxWidth: 1400, margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', height: 90, gap: 32 }}>

          {/* Logo */}
          <Link to="/">
            <img 
              src="/logo.png" 
              alt="Abstractivo.art" 
              style={{ height: 80 }} 
            />
          </Link>

          {/* Navigation desktop */}
          <nav className="nav-desktop" style={{ gap: 32, flex: 1, alignItems: 'center' }}>
            {NAV_LINKS.map(({ to, label }) => (
              <Link key={to} to={to} className={'nav-link' + (location.pathname + location.search === to ? ' active' : '')}>{label}</Link>
            ))}
          </nav>

          {/* Actions desktop */}
          <div className="nav-actions-desktop" style={{ alignItems: 'center', gap: 16, flexShrink: 0 }}>

            {/* Sélecteur de langue */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 0, border: '1px solid #E8DFCC', overflow: 'hidden' }}>
              {['fr', 'en'].map((lang) => (
                <button key={lang} onClick={() => switchLang(lang)} style={{
                  background: currentLang === lang ? '#2C2416' : 'transparent',
                  color: currentLang === lang ? '#F5EDDB' : '#8B6F4E',
                  border: 'none', cursor: 'pointer',
                  fontFamily: "'Raleway', sans-serif", fontSize: 10, fontWeight: 500,
                  letterSpacing: 1.5, textTransform: 'uppercase',
                  padding: '6px 10px', transition: 'all 0.2s', lineHeight: 1,
                }}>
                  {lang}
                </button>
              ))}
            </div>

            {/* Cloche notifications */}
            {user && (
              <div ref={notifRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowNotifs(p => !p)}
                  aria-label={t('notifications.title')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: showNotifs ? '#2C2416' : '#8B6F4E', transition: 'color 0.2s' }}
                >
                  {/* Icône cloche SVG filaire */}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                  {/* Badge rouge */}
                  {unread > 0 && (
                    <span style={{
                      position: 'absolute', top: 2, right: 2,
                      background: '#C0392B', color: '#fff',
                      borderRadius: '50%', minWidth: 15, height: 15,
                      fontSize: 8, fontFamily: "'Raleway', sans-serif", fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      padding: '0 3px', lineHeight: 1,
                      border: '1.5px solid #FDFAF5',
                    }}>
                      {unread > 9 ? '9+' : unread}
                    </span>
                  )}
                </button>

                {/* Panneau dropdown */}
                {showNotifs && (
                  <div style={{
                    position: 'absolute', right: 0, top: 'calc(100% + 10px)',
                    width: 300, background: '#FDFAF5',
                    border: '1px solid #E8DFCC',
                    boxShadow: '0 12px 40px rgba(44,36,22,0.13)',
                    zIndex: 200,
                  }}>
                    {/* En-tête */}
                    <div style={{ padding: '13px 16px', borderBottom: '1px solid #E8DFCC', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: 9, letterSpacing: 3, textTransform: 'uppercase', color: '#2C2416' }}>
                        {t('notifications.title')} {unread > 0 && <span style={{ color: '#C0392B', marginLeft: 4 }}>· {unread}</span>}
                      </span>
                      {notifications.length > 0 && (
                        <button onClick={clearAll} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Raleway', sans-serif", fontSize: 10, color: '#A89070', padding: 0, transition: 'color 0.2s' }}
                          onMouseEnter={e => e.target.style.color = '#5C4A2A'}
                          onMouseLeave={e => e.target.style.color = '#A89070'}>
                          {t('notifications.clearAll')}
                        </button>
                      )}
                    </div>

                    {/* Liste */}
                    {notifications.length === 0 ? (
                      <div style={{ padding: '28px 16px', textAlign: 'center' }}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#D4C5A9" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', margin: '0 auto 10px' }}>
                          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                        </svg>
                        <p style={{ fontFamily: "'Raleway', sans-serif", fontSize: 12, color: '#A89070' }}>{t('notifications.empty')}</p>
                      </div>
                    ) : (
                      <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                        {notifications.map(n => (
                          <div key={n.id} style={{ padding: '12px 16px', borderBottom: '1px solid #F5EDDB', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                            {/* Icône type */}
                            <div style={{ flexShrink: 0, marginTop: 1 }}>
                              {n.type === 'sale' ? (
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#D4A017" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
                                </svg>
                              ) : (
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#5C8A5C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="20 6 9 17 4 12"/>
                                </svg>
                              )}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p style={{ fontFamily: "'Raleway', sans-serif", fontSize: 12, color: '#2C2416', margin: '0 0 3px', lineHeight: 1.5 }}>{n.message}</p>
                              {n.type === 'sale' && n.amount && (
                                <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 13, fontStyle: 'italic', color: '#8B6F4E', margin: 0 }}>
                                  {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n.amount)}
                                </p>
                              )}
                            </div>
                            <button onClick={() => dismiss(n.id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#D4C5A9', flexShrink: 0, padding: '2px', lineHeight: 1, transition: 'color 0.15s' }}
                              onMouseEnter={e => e.target.style.color = '#8B6F4E'}
                              onMouseLeave={e => e.target.style.color = '#D4C5A9'}>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {user ? (
              <>
                {user.role === 'artist' && (
                  <Link to="/nouvelle-oeuvre" className="btn-primary" style={{ padding: '9px 18px' }}>{t('nav.publish')}</Link>
                )}
                <div ref={userMenuRef} style={{ position: 'relative' }}>
                  <button onClick={() => setUserMenuOpen(!userMenuOpen)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: '6px 8px' }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#E8DFCC', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#8B6F4E' }}>
                      {user.avatar_url
                        ? <img src={user.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : user.full_name?.[0]?.toUpperCase()}
                    </div>
                    <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: 13, color: '#5C4A2A' }}>{user.full_name?.split(' ')[0]}</span>
                    <span style={{ color: '#8B6F4E', fontSize: 10 }}>▾</span>
                  </button>
                  {userMenuOpen && (
                    <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 8, background: '#FDFAF5', border: '1px solid #E8DFCC', boxShadow: '0 8px 32px rgba(44,36,22,0.12)', minWidth: 180, zIndex: 200 }}>
                      <Link to="/tableau-de-bord" style={{ display: 'block', padding: '13px 20px', fontFamily: "'Raleway', sans-serif", fontSize: 12, letterSpacing: 1, color: '#5C4A2A', borderBottom: '1px solid #F0E8D5' }}>{t('nav.dashboard')}</Link>
                      {user.username && <Link to={'/artiste/' + user.username} style={{ display: 'block', padding: '13px 20px', fontFamily: "'Raleway', sans-serif", fontSize: 12, letterSpacing: 1, color: '#5C4A2A', borderBottom: '1px solid #F0E8D5' }}>{t('nav.profile')}</Link>}
                      {user.role === 'admin' && <Link to="/admin" style={{ display: 'block', padding: '13px 20px', fontFamily: "'Raleway', sans-serif", fontSize: 12, letterSpacing: 1, color: '#8B3A3A', borderBottom: '1px solid #F0E8D5' }}>{t('nav.admin')}</Link>}
                      <button onClick={handleLogout} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '13px 20px', fontFamily: "'Raleway', sans-serif", fontSize: 12, letterSpacing: 1, color: '#C0392B', background: 'none', border: 'none', cursor: 'pointer' }}>{t('nav.logout')}</button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to="/connexion" className="btn-outline" style={{ padding: '9px 18px' }}>{t('nav.login')}</Link>
                <Link to="/inscription" className="btn-primary" style={{ padding: '9px 18px' }}>{t('nav.register')}</Link>
              </>
            )}
          </div>

          {/* Bouton burger (mobile) */}
          <button className="burger-btn" onClick={() => setBurgerOpen(!burgerOpen)}
            aria-label={burgerOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, marginLeft: 'auto', flexShrink: 0, flexDirection: 'column', gap: 5, alignItems: 'flex-end' }}>
            <span style={{ display: 'block', height: 1.5, background: '#2C2416', transition: 'all 0.3s',
              width: 24, transform: burgerOpen ? 'translateY(6.5px) rotate(45deg)' : 'none' }} />
            <span style={{ display: 'block', height: 1.5, background: '#2C2416', transition: 'all 0.3s',
              width: burgerOpen ? 0 : 18, opacity: burgerOpen ? 0 : 1 }} />
            <span style={{ display: 'block', height: 1.5, background: '#2C2416', transition: 'all 0.3s',
              width: 24, transform: burgerOpen ? 'translateY(-6.5px) rotate(-45deg)' : 'none' }} />
          </button>
        </div>
      </header>

      {/* ── MENU BURGER MOBILE ── */}
      <div className={'burger-overlay' + (burgerOpen ? ' open' : '')} style={{ paddingTop: 72 }}>
        {/* Header du menu */}
        <div style={{ padding: '20px 28px', borderBottom: '1px solid #E8DFCC', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: 9, letterSpacing: 4, textTransform: 'uppercase', color: '#8B6F4E' }}>{t('nav.menu')}</span>
          {/* Sélecteur langue dans burger */}
          <div style={{ display: 'flex', gap: 0, border: '1px solid #E8DFCC', overflow: 'hidden' }}>
            {['fr', 'en'].map((lang) => (
              <button key={lang} onClick={() => switchLang(lang)} style={{
                background: currentLang === lang ? '#2C2416' : 'transparent',
                color: currentLang === lang ? '#F5EDDB' : '#8B6F4E',
                border: 'none', cursor: 'pointer',
                fontFamily: "'Raleway', sans-serif", fontSize: 10, fontWeight: 500,
                letterSpacing: 1.5, textTransform: 'uppercase',
                padding: '6px 10px', transition: 'all 0.2s', lineHeight: 1,
              }}>
                {lang}
              </button>
            ))}
          </div>
        </div>

        {/* Liens navigation */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <span className="burger-section-title">{t('nav.explore')}</span>
          {NAV_LINKS.map(({ to, label }) => (
            <Link key={to} to={to} className="burger-nav-link">{label}</Link>
          ))}

          {user ? (
            <>
              <span className="burger-section-title">{t('nav.account')}</span>
              <Link to="/tableau-de-bord" className="burger-nav-link">{t('nav.dashboard')}</Link>
              {user.role === 'artist' && (
                <Link to="/nouvelle-oeuvre" className="burger-nav-link" style={{ color: '#2C7A53' }}>{t('nav.publishArtwork')}</Link>
              )}
              {user.username && <Link to={'/artiste/' + user.username} className="burger-nav-link">{t('nav.publicProfile')}</Link>}
              {user.role === 'admin' && <Link to="/admin" className="burger-nav-link" style={{ color: '#8B3A3A' }}>{t('nav.admin')}</Link>}
            </>
          ) : (
            <>
              <span className="burger-section-title">{t('nav.account')}</span>
              <Link to="/connexion" className="burger-nav-link">{t('nav.login')}</Link>
              <Link to="/inscription" className="burger-nav-link" style={{ color: '#2C2416', fontWeight: 600 }}>{t('nav.register')}</Link>
              <Link to="/inscription?role=artist" className="burger-nav-link" style={{ color: '#8B6F4E' }}>{t('nav.sellArt')}</Link>
              <Link to="/contact" className="burger-nav-link">Contact</Link>
            </>
          )}
        </div>

        {/* Footer du menu burger */}
        <div style={{ padding: '20px 28px', borderTop: '1px solid #E8DFCC', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: 11, color: '#8B6F4E' }}>{t('nav.commissionNote')}</span>
          {user && (
            <button onClick={handleLogout} style={{ fontFamily: "'Raleway', sans-serif", fontSize: 11, letterSpacing: 1, color: '#C0392B', background: 'none', border: 'none', cursor: 'pointer' }}>
              {t('nav.logout')}
            </button>
          )}
        </div>
      </div>

      {/* ── CONTENU PRINCIPAL ── */}
      <main style={{ paddingTop: 72 }}>
        <Outlet />
      </main>

      {/* ── FOOTER ── */}
      <footer style={{ background: '#2C2416', color: '#D4C5A9', padding: '60px 32px 40px', marginTop: 80 }}>
        <div style={{ maxWidth: 1400, margin: '0 auto' }}>
          <div className="footer-grid">
            <div>
              <div style={{ fontSize: 26, fontWeight: 300, letterSpacing: 4, fontStyle: 'italic', marginBottom: 16, color: '#F5EDDB' }}>Abstractivo</div>
              <p style={{ fontFamily: "'Raleway', sans-serif", fontSize: 13, lineHeight: 1.85, color: '#A89070', maxWidth: 280 }}>
                {t('footer.tagline')}
              </p>
              <p style={{ fontFamily: "'Raleway', sans-serif", fontSize: 12, lineHeight: 1.85, color: '#6B5840', maxWidth: 280, marginTop: 12 }}>
                {t('footer.shippingNote')}
              </p>
            </div>
            {[
              { titleKey: 'footer.cols.explore', links: [[t('footer.links.gallery'), '/galerie'], [t('footer.links.selection'), '/galerie?featured=true'], [t('footer.links.artists'), '/galerie']] },
              { titleKey: 'footer.cols.creators', links: [[t('footer.links.sell'), '/inscription?role=artist'], [t('footer.links.commission'), '#'], [t('footer.links.stripe'), '#']] },
              { titleKey: 'footer.cols.shipping', links: [[t('footer.links.shippingByArtist'), '#'], [t('footer.links.faq'), '/contact'], [t('footer.links.contact'), '/contact']] },
              { titleKey: 'footer.cols.legal', links: [[t('footer.links.about'), '/a-propos'], [t('footer.links.cgv'), '/cgv'], [t('footer.links.legal'), '/cgv']] },
            ].map(col => (
              <div key={col.titleKey}>
                <h4 style={{ fontFamily: "'Raleway', sans-serif", fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: '#8B6F4E', marginBottom: 20 }}>{t(col.titleKey)}</h4>
                {col.links.map(([label, href]) => (
                  <Link key={label} to={href}
                    style={{ display: 'block', fontFamily: "'Raleway', sans-serif", fontSize: 13, color: '#A89070', marginBottom: 10, transition: 'color 0.2s' }}
                    onMouseEnter={e => e.target.style.color = '#D4C5A9'}
                    onMouseLeave={e => e.target.style.color = '#A89070'}>
                    {label}
                  </Link>
                ))}
              </div>
            ))}
          </div>

          {/* Note livraison */}
          <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', padding: '16px 20px', marginBottom: 28, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 16, flexShrink: 0 }}>📦</span>
            <p style={{ fontFamily: "'Raleway', sans-serif", fontSize: 12, color: '#8B6F4E', lineHeight: 1.7 }}>
              {t('footer.shippingDisclaimer')}
            </p>
          </div>

          <div style={{ borderTop: '1px solid #4A3820', paddingTop: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <p style={{ fontFamily: "'Raleway', sans-serif", fontSize: 12, color: '#6B5840' }}>{t('footer.rights')}</p>
            <p style={{ fontFamily: "'Raleway', sans-serif", fontSize: 12, color: '#6B5840' }}>{t('footer.stripeNote')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
